import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../utilities";
import {
  selectFreeRoundsCompletionPending,
  selectFreeRoundsDisplayedWin,
  selectCurrency,
  selectSpinComplete,
  selectCounterAnimationFinished,
  selectWinAnimationsPlaying,
  selectCurrentRound,
  selectHoldAndWinActive,
  selectShowHoldAndWinOutro,
  selectHoldAndWinCurrentRound,
  selectPotCollectionActive,
  selectPotCollectionOutroTransitionActive,
  selectFreeSpinsActive,
  selectShowTransitionDialog,
  selectShowGambleDialog,
} from "../State/selectors";
import {
  openInfoPopup,
  openErrorPopup,
  setBet,
  stopAutoPlay,
  setPotCollectionOutroCounterFinished,
  clearFreeSpinsWin,
  applyReplayWin,
} from "../State/actions";
import {
  clearFreeRounds,
} from "../State/Slices/freeRoundsSlice";
import { clearFeatureStateToBase, syncGameState } from "../State/Slices/gameSettingsSlice";
import store from "../State/Store/store";
import StorageManager from "../utils/StorageManager";
import TickerTimeout from "../utils/TickerTimeout";

/**
 * useFreeRoundsCompletion — orchestrates popups after the last free round.
 *
 * Replaces FreeRoundsCompletionHandler (headless component).
 * Same pattern as pot collection's exit sequence:
 *   - localStorage is source of truth for reload recovery
 *   - Cleanup happens on next base game spin (clearCompletionData in RGS)
 *
 * Phase flow:
 *   null → "waiting" → "info_popup" → "warning_popup" → done (clears Redux state)
 *
 * Call this hook from ReelControlContext.
 */
const useFreeRoundsCompletion = () => {
  const dispatch = useDispatch();

  // Signals
  const completionPending = useSelector(selectFreeRoundsCompletionPending);
  const freeRoundsDisplayedWin = useSelector(selectFreeRoundsDisplayedWin);
  const currency = useSelector(selectCurrency);

  // Animation gates
  const spinComplete = useSelector(selectSpinComplete);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const currentRound = useSelector(selectCurrentRound);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);

  // Blocking gates
  const isHoldAndWinActive = useSelector(selectHoldAndWinActive);
  const showHoldAndWinOutro = useSelector(selectShowHoldAndWinOutro);
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const potCollectionOutroTransitionActive = useSelector(selectPotCollectionOutroTransitionActive);
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const showTransitionDialog = useSelector(selectShowTransitionDialog);
  const showGambleDialog = useSelector(selectShowGambleDialog);

  // Phase ref — survives re-renders, no need for useState (we don't need re-renders from this)
  const phaseRef = useRef(null);
  const delayTimeoutRef = useRef(null);

  // ── Completion flow: sequential popup chain ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runCompletionFlow = useCallback(() => {
    if (phaseRef.current !== "ready") return;
    phaseRef.current = "running";

    // 1. Stop autoplay if active
    const state = store.getState();
    if (state.settings.autoPlayActive) {
      dispatch(stopAutoPlay());
    }

    // 2. Show info popup: "You won XXX in free rounds"
    const formattedTotalWin = formatToCustomLocale(freeRoundsDisplayedWin);
    dispatch(
      openInfoPopup({
        key: "you_have_won_in_free_rounds",
        data: { N: `${formattedTotalWin} ${currency.toUpperCase()}` },
      }),
    );

    // 5. Wait for info popup dismiss → show warning popup → wait for dismiss → cleanup
    const unsubscribe = store.subscribe(() => {
      const currentState = store.getState();

      if (phaseRef.current === "running") {
        // Waiting for info popup to open
        if (currentState.userInterface.infoPopupActive) {
          phaseRef.current = "info_popup";
        }
      } else if (phaseRef.current === "info_popup") {
        // Waiting for info popup to close
        if (!currentState.userInterface.infoPopupActive) {
          phaseRef.current = "warning_popup";
          dispatch(openErrorPopup("206"));
        }
      } else if (phaseRef.current === "warning_popup") {
        // Waiting for warning popup to close
        if (!currentState.userInterface.errorPopupActive) {
          // All popups dismissed — cleanup
          unsubscribe();
          phaseRef.current = null;

          // Replay plays this whole sequence for authenticity — the popups,
          // the Total Win, the FR teardown — but must skip the two steps that
          // reach outside the replayed round into the player's real session.
          const isReplay = currentState.replay?.sessionActive;
          // Captured before clearFreeRounds below touches the slice.
          const frTotalWin = currentState.freeRounds?.displayedWin ?? 0;

          if (!isReplay) {
            // Restore bet: betToRestore was captured from base_game_round.bet at FR init
            // (before base_game_round got overwritten with the FR bet).
            // Skipped in replay — StorageManager holds the player's REAL
            // session, and replay must neither read its bet as authoritative
            // nor write the live bet (their live bet is restored on exit).
            const savedFR = StorageManager.loadFreeRoundsRound();
            const savedBaseGame = StorageManager.loadBaseGameRound();
            const betToRestore = savedFR?.betToRestore ?? savedBaseGame?.bet;
            const currentBets = currentState.game.bets;

            if (betToRestore && currentBets.includes(betToRestore)) {
              dispatch(setBet(betToRestore));
            }
          }

          // Clear all FR Redux state → selectFreeRoundsActive → false → UI tears down.
          // Safe in replay: both slices are snapshotted on session start and
          // handed back on exit, so this only tears down the replayed round's
          // indicators, not the player's live campaign.
          dispatch(clearFreeRounds());
          dispatch(clearFeatureStateToBase());

          // Clear stale PC outro flag so isPCMode doesn't activate after FR ends
          dispatch(setPotCollectionOutroCounterFinished(false));

          // ── And the free-spins total, for the same reason ──
          //
          // The line above, ported. A bonus won inside the campaign keeps its
          // total through resetFreeSpins so the Win row can show it — but the
          // exit has already added that total to the campaign's. Left in
          // place, it outlived the campaign: the moment clearFreeRounds ran,
          // the free-spins "done" mode took the row back and showed the bonus
          // total (5) instead of the campaign's (1869). Vladimir, 2026-09-24:
          // *"total win was proper, then it moved on 0.5 after i closed this
          // popup."* Cleared the way the next base spin clears it.
          dispatch(clearFreeSpinsWin());

          if (isReplay) {
            // Credit the campaign total to the replay balance — this is the
            // moment the live game's balance jumps, so the replay's must too.
            //
            // A campaign pays out ONCE at the end, not per round: WinDisplay
            // skips its per-spin balance update while freeRoundsActive is set
            // (wins only accumulate into the Total Win indicator), and live
            // settles up here via syncGameState. Replay can't call that — it
            // would fetch the player's real wallet, and a standalone link has
            // no API — so it credits the same figure directly.
            //
            // Safe to pass the full total: applyReplayWin adds only what
            // hasn't been credited yet, and nothing has been during the
            // campaign for exactly the reason above.
            dispatch(applyReplayWin(frTotalWin));
          } else {
            // Sync balance with server (full init — ensures FR win is reflected in balance)
            dispatch(syncGameState({ syncBalance: true }));
          }
        }
      }
    });
  }, [dispatch, freeRoundsDisplayedWin, currency]);

  // ── Main completion effect ──
  useEffect(() => {
    // Only run when completion is pending and we haven't started yet
    if (!completionPending || phaseRef.current !== null) {
      return;
    }

    // Wait for Hold & Win to finish if it's running during last FR round
    if (isHoldAndWinActive || showHoldAndWinOutro) {
      return;
    }

    // Wait for Pot Collection bonus to finish if it's running during last FR round
    if (potCollectionActive || potCollectionOutroTransitionActive) {
      return;
    }

    // ── And for FREE SPINS, the same way ──
    //
    // The line above, ported. Free spins won on the last free round come back
    // with `free_rounds.status: "played"` on the room's LAST spin, so the
    // popup opened over that spin, with the total-win popup and the exit still
    // to come. Vladimir, 2026-09-24: *"just like pot collection, after
    // transition we show outro popup... until we completely return to base
    // game."*
    //
    // `active` covers the room and its total-win popup — resetFreeSpins only
    // clears it at the exit wipe's midpoint — and `showTransitionDialog` covers
    // the wipe itself until its last frame. Together there is no gap between
    // the room and the base game.
    if (freeSpinsActive || showTransitionDialog) {
      return;
    }

    // ── And for the GAMBLE screen ──
    //
    // A gamble lost on the last free round comes back with
    // `free_rounds.status: "played"` on the loss answer itself, while the
    // belt is still turning. The popup opened over the gamble screen, with a
    // total that did not yet hold the round's kept win. Vladimir, 2026-09-24:
    // *"when we close gamble dialog, that's where we show the outro popup."*
    // The kept win is added the moment the loss is known, so by the close the
    // total is complete.
    if (showGambleDialog) {
      return;
    }

    // Check if last round had a regular win (not H&W)
    const wasHoldAndWinRound = holdAndWinCurrentRound?.win > 0;
    const hasRegularWin = currentRound?.win > 0 && !wasHoldAndWinRound;

    let animationsComplete = false;
    if (hasRegularWin) {
      // Win round: wait for counter + win animations to finish
      animationsComplete = spinComplete && counterAnimationFinished && !winAnimationsPlaying;
    } else if (wasHoldAndWinRound) {
      // H&W round: just need spin complete (counter played during H&W outro)
      animationsComplete = spinComplete;
    } else {
      // No win: wait for spin complete + small delay
      if (spinComplete && !delayTimeoutRef.current) {
        delayTimeoutRef.current = new TickerTimeout(() => {
          delayTimeoutRef.current = null;
          phaseRef.current = "ready";
          runCompletionFlow();
        }, 100);
        return;
      }
      return;
    }

    if (animationsComplete) {
      phaseRef.current = "ready";
      runCompletionFlow();
    }
  }, [
    completionPending,
    spinComplete,
    counterAnimationFinished,
    winAnimationsPlaying,
    currentRound,
    holdAndWinCurrentRound,
    isHoldAndWinActive,
    showHoldAndWinOutro,
    potCollectionActive,
    potCollectionOutroTransitionActive,
    freeSpinsActive,
    showTransitionDialog,
    showGambleDialog,
    runCompletionFlow,
  ]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      if (delayTimeoutRef.current) {
        if (typeof delayTimeoutRef.current.clear === "function") {
          delayTimeoutRef.current.clear();
        }
        delayTimeoutRef.current = null;
      }
    };
  }, []);
};

export default useFreeRoundsCompletion;
